import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {type FC, useMemo} from 'react'
import styles from './FindingCommentPreview.module.css'
import type {CommentPreviewData} from './types'

interface FindingCommentPreviewProps {
  preview: CommentPreviewData
  filePath?: string
  showSuggestionPaths?: boolean
  hideMessage?: boolean
}

export const FindingCommentPreview: FC<FindingCommentPreviewProps> = ({
  preview,
  filePath,
  showSuggestionPaths,
  hideMessage,
}) => {
  const {bodyHTML, diffHunk, suggestion} = preview

  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side
  const safeBodyHTML = bodyHTML as SafeHTMLString
  const suggestionDiffEntries = suggestion?.diffEntries ?? []
  const showDiff = !suggestion
  const diffHunkLines = useMemo(() => (showDiff && diffHunk ? parseUnifiedDiff(diffHunk) : []), [showDiff, diffHunk])

  return (
    <div className="d-flex flex-column gap-2" data-testid="finding-comment-preview">
      {!hideMessage && (
        <VerifiedHTMLDiv html={safeBodyHTML} className="markdown-body timeline-comment comment-body tmp-p-3 tmp-my-2" />
      )}
      {(diffHunkLines.length > 0 || suggestionDiffEntries.length > 0) && (
        <div className="d-flex flex-column gap-3">
          {diffHunkLines.length > 0 && (
            <div data-testid="finding-comment-diff-hunk">
              <div className="d-flex flex-column overflow-hidden border rounded-2">
                {filePath && (
                  <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
                    <span>{filePath}</span>
                  </div>
                )}
                <StaticUnifiedDiffPreview
                  tabSize={4}
                  className={styles.diffPreview}
                  diffTableClassName="borderColor-default"
                  diffLines={diffHunkLines}
                />
              </div>
            </div>
          )}

          {suggestionDiffEntries.map(entry => {
            const key = `${entry.path}-${entry.diffLines
              .map(line => `${line.left ?? ''}:${line.right ?? ''}:${line.type}:${line.text}`)
              .join('|')}`

            return (
              <div key={key} className="d-flex flex-column gap-2" data-testid="finding-comment-suggestion">
                <h4 className="text-small text-semibold color-fg-muted m-0">Suggested change</h4>
                <div className="d-flex flex-column overflow-hidden border rounded-2">
                  {showSuggestionPaths && (
                    <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
                      <span>{entry.path}</span>
                    </div>
                  )}
                  <StaticUnifiedDiffPreview
                    tabSize={4}
                    className={styles.diffPreview}
                    diffTableClassName="borderColor-default"
                    diffLines={entry.diffLines}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
